import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { caseStudies } from '@/data/caseStudies'
import { useSEO } from '@/hooks/useSEO'
import { SolutionsDropdown, SolutionsMobileMenu } from '@/components/marketing/SolutionsDropdown'
import { MarketingFooter } from '@/components/marketing/MarketingFooter'
import { EnterpriseCapabilities } from '@/components/marketing/EnterpriseCapabilities'
import { CONTACT_SALES_EMAIL, track } from '@/lib/analytics'
import {
  FileSearch,
  Layers,
  PenTool,
  Cpu,
  MessageSquare,
  Upload,
  Settings2,
  Sparkles,
  Check,
  ArrowRight,
  ChevronRight,
  Menu,
  X,
  ScrollText,
  Shield,
  Zap,
  RefreshCw,
  TrendingUp,
  Wand2,
  BookmarkPlus,
} from 'lucide-react'

// ──────────────────────────────────────────────────────────────────────────
// Homepage JSON-LD. These schemas describe the product, the onboarding flow,
// and the homepage FAQ. They live here (and are scoped to the Landing route
// via useSEO) so they don't appear on every prerendered page.
// ──────────────────────────────────────────────────────────────────────────

// Used for canonical + Organization schema. Trailing slash matches what
// Google ends up indexing for the bare host — keeping them aligned
// prevents a one-step canonical mismatch between sitemap, schema URL,
// and canonical tag.
const SITE_URL = 'https://documentiq.algoscale.com/'

const softwareApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'DocumentIQ',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  url: SITE_URL,
  description:
    'AI-powered document intelligence platform for extracting structured data from PDFs and Word documents at scale using LLMs.',
  offers: [
    { '@type': 'Offer', price: '0', priceCurrency: 'USD', name: 'Free Plan' },
    {
      '@type': 'Offer',
      price: '49',
      priceCurrency: 'USD',
      priceValidUntil: '2027-12-31',
      name: 'Starter Plan',
      description: '500 pages per month',
    },
    {
      '@type': 'Offer',
      price: '399',
      priceCurrency: 'USD',
      priceValidUntil: '2027-12-31',
      name: 'Business Plan',
      description: '5,000 pages per month',
    },
  ],
  creator: { '@type': 'Organization', name: 'Algoscale', url: 'https://algoscale.com' },
  featureList: [
    'Pre-built field templates for invoices, contracts, BOMs, COAs, BOLs, KYC, FNOL, and more',
    'Custom organization templates - save your field configurations for reuse',
    'Single-pass AI extraction with GPT-5, Claude, and more',
    'Multi-row line item extraction for invoices and contracts',
    'PDF annotations with raw text and correct extraction values',
    '3-mode project chat (documents, extracted data, or both)',
    'Auto-generated document summaries and analytics',
    'Real-time processing with confidence scores',
    'Export to CSV, Excel, and PDF',
    'Feedback loops and re-extraction with corrections',
    'DOCX and DOC support with automatic PDF conversion for full annotation parity',
    'Multi-tenant with RBAC and white-label branding',
    'Enterprise billing with custom invoicing',
    'Continuous-scroll PDF viewer with annotation overlay',
  ],
}

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to extract structured data from documents using AI',
  description:
    'Get from raw documents to structured data in four simple steps using DocumentIQ.',
  step: [
    {
      '@type': 'HowToStep',
      position: 1,
      name: 'Upload Documents',
      text: 'Drag and drop PDFs, DOC, or DOCX files. Documents are stored securely on Azure and ready for processing instantly.',
    },
    {
      '@type': 'HowToStep',
      position: 2,
      name: 'Configure Fields',
      text: 'Define what to extract with custom field schemas. AI auto-suggests extraction instructions. Add annotations for tricky fields.',
    },
    {
      '@type': 'HowToStep',
      position: 3,
      name: 'Extract and Review',
      text: 'Choose your LLM, hit extract, and watch results stream in real time. Review in a rich table, provide feedback, and re-extract to refine.',
    },
    {
      '@type': 'HowToStep',
      position: 4,
      name: 'Chat and Export',
      text: 'Ask questions across all your documents and extracted data. Export results to CSV, Excel, or PDF. Build on top with the full API.',
    },
  ],
}

const faqPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is DocumentIQ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'DocumentIQ is an AI-powered document intelligence platform that extracts structured data from PDFs and Word documents using large language models like GPT-5 and Claude. You define what fields to extract, upload your documents, and the AI returns structured, reviewable results with confidence scores.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I have to define every extraction field from scratch?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. DocumentIQ ships with 10 pre-built field templates for common document types: commercial invoices, purchase orders, master agreements, price escalation clauses, bills of lading, packing declarations, bills of materials, certificates of analysis, FNOL insurance claims, and KYC identity documents. Apply one with a click and your project is ready to extract immediately. You can also save your own field configurations as custom templates for your organization to reuse across projects.',
      },
    },
    {
      '@type': 'Question',
      name: 'How is DocumentIQ different from traditional OCR?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Traditional OCR converts images to text but doesn't understand what the text means. DocumentIQ sends entire documents to LLMs that understand context, tables, and relationships — extracting specific fields like invoice totals, contract clauses, or line items. It handles varied layouts, scanned documents, and complex tables without custom templates.",
      },
    },
    {
      '@type': 'Question',
      name: 'What file formats does DocumentIQ support?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'DocumentIQ supports PDF, DOC, and DOCX files. PDFs are sent directly to the LLM (including scanned/image PDFs when using models with vision capabilities). Word documents are parsed and text-extracted automatically.',
      },
    },
    {
      '@type': 'Question',
      name: 'Which AI models can I use for extraction?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'DocumentIQ supports multiple LLM providers through LiteLLM, including OpenAI (GPT-5, GPT-4o, GPT-4o Mini), Anthropic (Claude), and custom fine-tuned models. Your admin assigns which models are available on your billing plan, each with independent credit rates.',
      },
    },
    {
      '@type': 'Question',
      name: 'What are annotations and how do they improve accuracy?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Annotations let you draw bounding boxes on a PDF to show the AI exactly where a value appears. You provide both the raw text (what's in the document) and the correct extraction (what the AI should return). These become few-shot examples that dramatically improve accuracy across similar documents — like teaching a new employee where to look.",
      },
    },
    {
      '@type': 'Question',
      name: 'Can DocumentIQ extract line items and tables?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Multi-row line item extraction automatically detects and extracts repeating rows like invoice line items, bill of materials entries, or contract clauses. There are no row limits — it scales to hundreds of items per document.',
      },
    },
    {
      '@type': 'Question',
      name: 'How does the project chat assistant work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Each project has an AI chat interface with three context modes: Documents (searches raw document text via RAG), Extracted Data (queries your structured extraction results), or Both. The assistant cites specific documents, renders markdown, and you can export conversations as PDF, Markdown, or JSON.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is DocumentIQ suitable for enterprise use?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. DocumentIQ offers multi-tenant architecture with RBAC (owner, admin, member, viewer, billing roles), multi-organization support, white-label branding, custom offline invoicing, fine-tuned model support, and is deployed on Azure with enterprise-grade security. SSO/SAML integration is on the roadmap.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does DocumentIQ cost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'DocumentIQ has four tiers, billed per page extracted: Free ($0 — 50 pages lifetime, no card required), Starter ($49/month — 500 pages, all standard LLMs, chat, annotations), Business ($399/month — 5,000 pages, larger team and project limits), and Enterprise (custom pricing — unlimited pages, fine-tuned models, white-label, SSO). Premium models like GPT-4o consume more credits per page than standard models.',
      },
    },
  ],
}

// Service schema — clarifies for crawlers that DocumentIQ is a B2B service
// (the SoftwareApplication schema above describes the *product*; this one
// describes the *service* being offered, which is what Google Rich Results
// and AI answer engines look for to populate service-style cards).
// Each itemListElement.url points at the canonical solution/function page
// so the offer catalog is internally cross-linked and crawlable.
const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'DocumentIQ — AI-powered intelligent document processing',
  serviceType: 'Intelligent Document Processing',
  url: SITE_URL,
  description:
    'DocumentIQ extracts structured data from invoices, contracts, KYC documents, financial statements, insurance claims, bills of lading, customs declarations, and other business documents using modern AI and large language models.',
  provider: {
    '@type': 'Organization',
    name: 'Algoscale',
    legalName: 'Algoscale Technologies',
    url: 'https://algoscale.com',
    email: 'askus@algoscale.com',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'Sales',
        email: 'askus@algoscale.com',
        availableLanguage: ['English'],
      },
      {
        '@type': 'ContactPoint',
        contactType: 'Customer Support',
        email: 'askus@algoscale.com',
        availableLanguage: ['English'],
      },
    ],
  },
  areaServed: { '@type': 'Place', name: 'Worldwide' },
  audience: { '@type': 'BusinessAudience', audienceType: 'B2B' },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Document intelligence services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Accounts payable automation', url: `${SITE_URL}solutions/function/ap-automation` } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Contract intelligence', url: `${SITE_URL}solutions/function/contract-intelligence` } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'KYC and financial document processing', url: `${SITE_URL}solutions/industry/financial-services` } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Insurance claims processing', url: `${SITE_URL}solutions/function/claims-processing` } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Customs and trade compliance', url: `${SITE_URL}solutions/function/customs-trade-compliance` } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Manufacturing document extraction', url: `${SITE_URL}solutions/industry/manufacturing` } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Logistics and shipping document extraction', url: `${SITE_URL}solutions/industry/logistics` } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Quality and compliance document extraction', url: `${SITE_URL}solutions/function/quality-compliance` } },
    ],
  },
  keywords: [
    'Document AI',
    'Intelligent Document Processing',
    'Invoice Processing',
    'Contract Analysis',
    'OCR Alternative',
    'Document Extraction',
    'AI Data Extraction',
    'KYC Automation',
    'Financial Document Processing',
    'Logistics Document Automation',
  ].join(', '),
}

// Organization schema — gives Google a stable entity to build the brand
// knowledge panel from. `sameAs` links out to the canonical brand profiles
// so the disambiguator can collapse them into one entity.
const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}#organization`,
  name: 'Algoscale',
  legalName: 'Algoscale Technologies',
  alternateName: 'DocumentIQ by Algoscale',
  url: SITE_URL,
  logo: `${SITE_URL}algoscale-logo.png`,
  description:
    'Algoscale builds DocumentIQ, an AI-powered intelligent document processing platform that extracts structured data from invoices, contracts, KYC documents, and other business documents.',
  email: 'askus@algoscale.com',
  sameAs: [
    'https://algoscale.com',
    'https://www.linkedin.com/company/algoscale',
    'https://twitter.com/algoscale',
  ],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'Sales',
      email: 'askus@algoscale.com',
      availableLanguage: ['English'],
      areaServed: 'Worldwide',
    },
    {
      '@type': 'ContactPoint',
      contactType: 'Customer Support',
      email: 'askus@algoscale.com',
      availableLanguage: ['English'],
      areaServed: 'Worldwide',
    },
  ],
}

// WebSite schema with SearchAction — opt-in to the SERP sitelinks search
// box. The `target` URL template tells Google how to deep-link a query
// into our search/blog filter.
const webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}#website`,
  url: SITE_URL,
  name: 'DocumentIQ',
  publisher: { '@id': `${SITE_URL}#organization` },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}blog?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
  inLanguage: 'en-US',
}

const landingJsonLd = [
  organizationSchema,
  webSiteSchema,
  softwareApplicationSchema,
  serviceSchema,
  howToSchema,
  faqPageSchema,
]

const features = [
  {
    icon: Wand2,
    title: 'Pre-Built Field Templates',
    description:
      'Start projects in seconds with 10 ready-made templates: invoices, contracts, BOMs, COAs, bills of lading, KYC, FNOL, and more. Save your own field sets as reusable templates per organization.',
  },
  {
    icon: FileSearch,
    title: 'Single-Pass AI Extraction',
    description:
      'Send entire PDFs directly to GPT-5, Claude, and more. All fields extracted in one pass — no text pre-processing needed. Handles scanned documents, tables, and complex layouts.',
  },
  {
    icon: Layers,
    title: 'Multi-Row Line Item Extraction',
    description:
      'Automatically detects and extracts repeating rows like invoice line items, order details, or contract clauses. No row limits — scales to hundreds of items per document.',
  },
  {
    icon: PenTool,
    title: 'Smart Annotations',
    description:
      'Draw bounding boxes on PDFs to teach the AI exactly where to look. Set raw text and correct extraction values as few-shot examples. Enable or disable annotations per extraction run.',
  },
  {
    icon: Cpu,
    title: 'Multi-Model Support',
    description:
      'Choose from OpenAI, Anthropic, and custom fine-tuned models per job. Admin assigns models per billing plan with independent credit rates. Compare outputs across providers.',
  },
  {
    icon: RefreshCw,
    title: 'Feedback & Re-Extraction',
    description:
      'Mark extractions as correct, incorrect, or corrected. Re-process selected documents with corrections injected as ground truth. Continuously improve accuracy.',
  },
]

const steps = [
  {
    icon: Upload,
    number: '01',
    title: 'Upload Documents',
    description:
      'Drag and drop PDFs, DOC, or DOCX files. Documents are stored securely on Azure and ready for processing instantly.',
  },
  {
    icon: Settings2,
    number: '02',
    title: 'Pick a Template or Configure Fields',
    description:
      'Start from 10 pre-built templates (invoices, contracts, BOMs, COAs, BOLs, KYC, FNOL, and more) — or define your own fields. AI auto-suggests extraction instructions.',
  },
  {
    icon: PenTool,
    number: '03',
    title: 'Annotate (optional)',
    description:
      'Draw bounding boxes on a representative PDF to teach the AI which regions map to which fields. Becomes few-shot ground-truth for every similar document thereafter.',
  },
  {
    icon: Sparkles,
    number: '04',
    title: 'Extract & Review',
    description:
      'Choose your LLM, hit extract, and watch results stream in real time. Review in a rich table, provide feedback, and re-extract to refine.',
  },
  {
    icon: MessageSquare,
    number: '05',
    title: 'Chat & Export',
    description:
      'Ask questions across all your documents and extracted data. Export results to CSV, Excel, or PDF. Build on top with the full API.',
  },
]

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: '',
    description: '50 pages free, lifetime — no card required',
    features: [
      '50 pages total (one-time grant)',
      '2 projects, 10 fields per project',
      'Standard LLM (GPT-4o Mini)',
      'CSV export',
      'Community support',
    ],
    cta: 'Start Free',
    ctaLink: '/register',
    highlighted: false,
  },
  {
    name: 'Starter',
    price: '$49',
    period: '/mo',
    description: 'For teams getting serious about extraction',
    features: [
      '500 pages per month',
      '10 projects',
      'All standard LLMs',
      'Project chat assistant',
      'PDF annotations & feedback loops',
      'Excel, CSV & PDF export',
      'Email support',
    ],
    cta: 'Start Trial',
    ctaLink: '/register',
    highlighted: true,
    badge: 'Most Popular',
  },
  {
    name: 'Business',
    price: '$399',
    period: '/mo',
    description: 'Built for production document volume',
    features: [
      '5,000 pages per month',
      'Unlimited projects',
      'Premium LLMs (GPT-4o, Claude Opus)',
      'Document summaries & analytics',
      'Multi-row line item extraction',
      'White-label branding',
      'Priority email support',
    ],
    cta: 'Start Trial',
    ctaLink: '/register',
    highlighted: false,
    badge: 'Best Value',
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    description: 'For organizations with custom requirements',
    features: [
      'Everything in Business',
      'Unlimited pages — custom pricing',
      'Private fine-tuned models',
      'Custom offline invoicing',
      'Dedicated account manager',
      'SSO / SAML integration',
      'On-premise deployment option',
    ],
    cta: 'Contact Sales',
    ctaLink: 'mailto:askus@algoscale.com',
    highlighted: false,
  },
]

const trustedBy = [
  'Contract Management',
  'Invoice Processing',
  'Legal Document Review',
  'Insurance Claims',
  'Real Estate Leases',
  'Financial Reports',
]

export default function Landing() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useSEO({
    title: 'AI Document Data Extraction Software | Algoscale',
    description:
      'AI-powered automated data capture for invoices, contracts, forms, and PDFs. Extract structured document data quickly and accurately with Algoscale.',
    canonical: SITE_URL,
    ogType: 'website',
    ogImage: '/og/home.png',
    jsonLd: landingJsonLd,
  })

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false)
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      {/* ── Header ─────────────────────────────────────────────── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-18">
            <div className="flex items-center gap-2.5">
              <img
                src="/algoscale-logo.png"
                alt="Algoscale"
                className="h-8 w-auto"
              />
              <span className="text-lg font-semibold tracking-tight text-gray-900">
                DocumentIQ
              </span>
            </div>

            <nav className="hidden md:flex items-center gap-8">
              <button
                onClick={() => scrollToSection('features')}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection('how-it-works')}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                How it Works
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                Pricing
              </button>
              <SolutionsDropdown />
              <Link
                to="/case-studies"
                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                Case Studies
              </Link>
              <Link
                to="/blog"
                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                Blog
              </Link>
            </nav>

            <div className="hidden md:flex items-center gap-4">
              <Link
                to="/login"
                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
              >
                Start for Free
              </Link>
            </div>

            <button
              className="md:hidden p-2 text-gray-600 hover:text-gray-900"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-gray-200 shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <button
                onClick={() => scrollToSection('features')}
                className="block w-full text-left text-sm font-medium text-gray-600 hover:text-gray-900 py-2"
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection('how-it-works')}
                className="block w-full text-left text-sm font-medium text-gray-600 hover:text-gray-900 py-2"
              >
                How it Works
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="block w-full text-left text-sm font-medium text-gray-600 hover:text-gray-900 py-2"
              >
                Pricing
              </button>
              <SolutionsMobileMenu onNavigate={() => setMobileMenuOpen(false)} />
              <Link
                to="/case-studies"
                className="block text-sm font-medium text-gray-600 hover:text-gray-900 py-2"
              >
                Case Studies
              </Link>
              <Link
                to="/blog"
                className="block text-sm font-medium text-gray-600 hover:text-gray-900 py-2"
              >
                Blog
              </Link>
              <hr className="border-gray-100" />
              <Link
                to="/login"
                className="block text-sm font-medium text-gray-600 hover:text-gray-900 py-2"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="block w-full text-center px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Start for Free
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ── Hero ───────────────────────────────────────────────── */}
      <section className="relative pt-28 sm:pt-36 pb-20 sm:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50/80 via-blue-50/30 to-white" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-blue-100/40 rounded-full blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium mb-6">
            <Sparkles size={14} />
            AI-Powered Document Intelligence
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-tight">
            Turn Documents into
            <br />
            <span className="text-blue-600">Structured Data</span>
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Upload PDFs, define what to extract, and let AI do the rest.
            Single-pass extraction with GPT-5, Claude, and more — handles tables,
            line items, and complex layouts out of the box.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors shadow-md shadow-blue-600/20 text-sm sm:text-base"
            >
              Start for Free
              <ArrowRight size={18} />
            </Link>
            <button
              onClick={() => scrollToSection('how-it-works')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors text-sm sm:text-base"
            >
              See How It Works
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Enterprise one-liner — fake-door for two unbuilt capabilities.
              Subtle below the primary CTAs so it doesn't steal focus from
              the free-signup conversion. Tracks distinct event so we can
              measure interest in fine-tuning vs. email intake separately
              from the broader contact-sales rate. */}
          <p className="mt-6 inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm text-gray-500">
            <Shield size={14} className="text-gray-400" />
            <span>
              <span className="font-medium text-gray-700">Enterprise:</span>{' '}
              private fine-tuning on your data &middot; email inbox intake &middot;{' '}
              <a
                href={`mailto:${CONTACT_SALES_EMAIL}?subject=${encodeURIComponent('Enterprise interest')}`}
                onClick={() => track('enterprise_inquiry_click', { capability: 'both', source: 'hero' })}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Talk to sales →
              </a>
            </span>
          </p>

          {/* Use case tags */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {trustedBy.map((item) => (
              <span
                key={item}
                className="px-3 py-1.5 rounded-full bg-gray-100 text-gray-500 text-xs font-medium"
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Key Stats ──────────────────────────────────────────── */}
      <section className="py-12 bg-white border-y border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600">1 Call</div>
              <div className="text-sm text-gray-500 mt-1">Per document extraction</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600">PDF + DOC</div>
              <div className="text-sm text-gray-500 mt-1">File formats supported</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600">Real-time</div>
              <div className="text-sm text-gray-500 mt-1">Progress tracking</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600">Multi-LLM</div>
              <div className="text-sm text-gray-500 mt-1">OpenAI, Anthropic & more</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ───────────────────────────────────────────── */}
      <section id="features" className="py-20 sm:py-28 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              Everything you need for
              <br />
              document intelligence
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              From extraction to chat to export — a complete platform for turning
              unstructured documents into actionable, structured data.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="group relative p-6 sm:p-8 rounded-2xl border border-gray-200 bg-white hover:border-blue-200 hover:shadow-lg hover:shadow-blue-50 transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-blue-50 text-blue-600 mb-5 group-hover:bg-blue-100 transition-colors">
                  <feature.icon size={22} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it Works ───────────────────────────────────────── */}
      <section
        id="how-it-works"
        className="py-20 sm:py-28 bg-gray-50 scroll-mt-20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              How it works
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Get from raw documents to structured data in five simple steps.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-6">
            {steps.map((step, index) => (
              <div key={step.number} className="relative flex flex-col items-center text-center">
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-[60%] w-[80%] h-px bg-gradient-to-r from-blue-300 to-blue-100" />
                )}

                <div className="relative z-10 inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white border-2 border-blue-200 shadow-sm mb-6">
                  <step.icon size={28} className="text-blue-600" />
                  <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">
                    {step.number}
                  </span>
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── End-to-end pipeline (system integration view) ──────
          Bridges the per-user "How it works" step icons into the
          systems-integration story: where docs come from, what
          DocumentIQ does in the middle, and where structured data
          gets pushed afterwards. SEO copy hits the source/destination
          names (Outlook, SharePoint, NetSuite, SAP, Snowflake) so
          the section ranks for "extract data to <platform>" and
          "document automation for <platform>" long-tails. Background
          is a subtle blue-to-white gradient — signals "architecture
          view" without duplicating the bg-white screenshot sections
          that bracket it. */}
      <section className="py-20 sm:py-28 bg-gradient-to-b from-blue-50/40 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium mb-4">
              <Zap size={14} />
              End-to-end automation
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              From your inbox to your ERP &mdash; fully automated
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              DocumentIQ slots into your existing stack. Pull documents
              from Outlook, SharePoint, Google Drive, Dropbox, or S3.
              Extract structured data with AI &mdash; including line
              items, signatures, and conditional fields. Push the
              results into NetSuite, SAP, Salesforce, Snowflake, or
              any system via REST API or webhook.
            </p>
          </div>

          {/* Diagram */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-lg shadow-gray-200/40 overflow-hidden mb-12">
            <img
              src="/screenshots/diagram-pipeline.svg"
              alt="DocumentIQ end-to-end pipeline: ingest documents from email, SharePoint, Google Drive, Dropbox, S3, Salesforce, or REST API; extract structured data with AI; push results to NetSuite, SAP, QuickBooks, Salesforce, Snowflake, BigQuery, webhook, or CSV / Excel export."
              width="1200"
              height="750"
              loading="lazy"
              decoding="async"
              className="w-full h-auto block"
            />
          </div>

          {/* Two-column source + destination keyword surface for SEO */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                Sources DocumentIQ pulls from
              </h3>
              <ul className="space-y-2.5 text-sm text-gray-700">
                <li className="flex items-start gap-2"><Check size={16} className="text-blue-600 mt-0.5 flex-shrink-0" /><span><span className="font-medium text-gray-900">Email mailboxes</span> &mdash; Outlook, Gmail, Microsoft 365 with auto-attachment ingest</span></li>
                <li className="flex items-start gap-2"><Check size={16} className="text-blue-600 mt-0.5 flex-shrink-0" /><span><span className="font-medium text-gray-900">SharePoint &amp; OneDrive</span> &mdash; folder watchers and new-file triggers</span></li>
                <li className="flex items-start gap-2"><Check size={16} className="text-blue-600 mt-0.5 flex-shrink-0" /><span><span className="font-medium text-gray-900">Google Drive, Dropbox, Box</span> &mdash; shared drive webhooks</span></li>
                <li className="flex items-start gap-2"><Check size={16} className="text-blue-600 mt-0.5 flex-shrink-0" /><span><span className="font-medium text-gray-900">Amazon S3 &amp; Azure Blob</span> &mdash; bucket prefixes, lifecycle events</span></li>
                <li className="flex items-start gap-2"><Check size={16} className="text-blue-600 mt-0.5 flex-shrink-0" /><span><span className="font-medium text-gray-900">Salesforce &amp; HubSpot attachments</span> &mdash; pull from any CRM record</span></li>
                <li className="flex items-start gap-2"><Check size={16} className="text-blue-600 mt-0.5 flex-shrink-0" /><span><span className="font-medium text-gray-900">REST API uploads</span> &mdash; programmatic ingest from any internal app</span></li>
              </ul>
            </div>

            <div className="rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                Destinations DocumentIQ pushes to
              </h3>
              <ul className="space-y-2.5 text-sm text-gray-700">
                <li className="flex items-start gap-2"><Check size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" /><span><span className="font-medium text-gray-900">NetSuite</span> &mdash; AP, AR, GL journal posting from extracted invoices</span></li>
                <li className="flex items-start gap-2"><Check size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" /><span><span className="font-medium text-gray-900">SAP S/4HANA</span> &mdash; MIRO invoice receipt, MIGO three-way match</span></li>
                <li className="flex items-start gap-2"><Check size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" /><span><span className="font-medium text-gray-900">QuickBooks &amp; Xero</span> &mdash; auto-create bills and match vendors</span></li>
                <li className="flex items-start gap-2"><Check size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" /><span><span className="font-medium text-gray-900">Salesforce records</span> &mdash; push contract terms onto Account or Opportunity</span></li>
                <li className="flex items-start gap-2"><Check size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" /><span><span className="font-medium text-gray-900">Snowflake &amp; BigQuery</span> &mdash; warehouse load for BI and reporting</span></li>
                <li className="flex items-start gap-2"><Check size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" /><span><span className="font-medium text-gray-900">Webhook &amp; REST API</span> &mdash; push to any internal system, plus scheduled CSV / Excel export</span></li>
              </ul>
            </div>
          </div>

          {/* Common patterns — keyword-dense narrative SEO surface */}
          <div className="mt-12 max-w-5xl mx-auto rounded-xl bg-gray-50 border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
              Common end-to-end patterns
            </h3>
            <ul className="space-y-3 text-sm text-gray-700 leading-relaxed">
              <li><span className="font-medium text-blue-700">Supplier invoice</span> arrives in a shared Outlook inbox &rarr; DocumentIQ extracts vendor, PO reference, line items, and totals &rarr; bill posts directly to <span className="font-medium text-emerald-700">NetSuite AP</span> with the right GL coding.</li>
              <li><span className="font-medium text-blue-700">Customer contract</span> drops into a SharePoint folder &rarr; DocumentIQ extracts escalation clauses, renewal dates, and obligation terms &rarr; structured fields land on the matching <span className="font-medium text-emerald-700">Salesforce Account</span>.</li>
              <li><span className="font-medium text-blue-700">Bill of lading</span> emailed from a carrier &rarr; DocumentIQ extracts shipper, consignee, HS codes, container counts &rarr; record created in your <span className="font-medium text-emerald-700">TMS or customs broker system</span> via webhook.</li>
              <li><span className="font-medium text-blue-700">Mill test certificate</span> arrives with a steel shipment &rarr; DocumentIQ extracts chemistry and mechanical properties &rarr; auto-validated against spec, results streamed into <span className="font-medium text-emerald-700">Snowflake</span> for compliance reporting.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ── Templates Highlight ────────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium mb-4">
                <Wand2 size={14} />
                Field Templates
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-4">
                Start in seconds,<br />not hours
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Skip the blank-canvas problem. DocumentIQ ships with 10 pre-built
                field templates covering the most common document workflows — each
                with field definitions, AI extraction prompts, and project context
                already configured. Apply one with a click, then customize as needed.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  'Invoices, POs, contracts, BOMs, COAs, BOLs, packing declarations, KYC, FNOL, and more',
                  'Multi-row line item structures encoded for tables and repeating data',
                  'Domain-specific extraction prompts (e.g., escalation clause types, ISO 6346 container numbers, CAS numbers)',
                  'Save your own custom fields as reusable templates for your team',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-gray-600">
                    <Check size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <BookmarkPlus size={14} />
                <span>Custom templates available on every plan</span>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white shadow-lg shadow-gray-200/40 overflow-hidden">
              <img
                src="/screenshots/screenshot-templates.svg"
                alt="DocumentIQ template picker — 8 pre-built field schemas including Commercial Invoice, Bill of Lading, Mill Test Cert, Customs Declaration"
                width="1200"
                height="750"
                loading="lazy"
                decoding="async"
                className="w-full h-auto block"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Fields Configuration Highlight ─────────────────────── */}
      <section className="py-20 sm:py-28 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1 rounded-2xl border border-gray-200 bg-white shadow-lg shadow-gray-200/40 overflow-hidden">
              <img
                src="/screenshots/screenshot-fields.svg"
                alt="DocumentIQ field manager — configure name, type, description, repeating flag, and the LLM extraction prompt per field"
                width="1200"
                height="750"
                loading="lazy"
                decoding="async"
                className="w-full h-auto block"
              />
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium mb-4">
                <Settings2 size={14} />
                Configure once
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-4">
                Define exactly what<br />you want extracted
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Every project has its own schema. Define each field's name, type, description,
                and a custom extraction prompt — and DocumentIQ writes the LLM instructions for
                you with a single click. Mark fields as repeating to capture multi-row tables
                with parent-child structure intact.
              </p>
              <ul className="space-y-3">
                {[
                  'Field types: text, number, date, boolean, list',
                  'AI-suggested extraction prompts you can edit or rewrite',
                  '"Repeating (line item)" toggle for invoices, BOMs, customs manifests',
                  'Reorder, duplicate, or save fields as a reusable template',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-gray-600">
                    <Check size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Extracted Data Highlight ───────────────────────────── */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium mb-4">
                <Sparkles size={14} />
                See results
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-4">
                Structured data,<br />with confidence scores
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Every extracted value carries a confidence score and a link back to the
                source page and bounding box. Review thousands of rows in a sortable table,
                flag bad extractions inline, and export to CSV or Excel — or pull the data
                via API into your ERP, CRM, or BI tool.
              </p>
              <ul className="space-y-3">
                {[
                  'Per-field confidence bars surface ambiguous extractions for review',
                  'Inline thumbs-up / thumbs-down feedback feeds the next extraction run',
                  'Summary cards: documents processed, fields extracted, credits used',
                  'Export to CSV, Excel, or pull via REST API',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-gray-600">
                    <Check size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white shadow-lg shadow-gray-200/40 overflow-hidden">
              <img
                src="/screenshots/screenshot-extraction.svg"
                alt="DocumentIQ extraction results — summary cards plus a table of extracted rows with confidence indicators and feedback buttons"
                width="1200"
                height="750"
                loading="lazy"
                decoding="async"
                className="w-full h-auto block"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Annotation Highlight ───────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1 rounded-2xl border border-gray-200 bg-white shadow-lg shadow-gray-200/40 overflow-hidden">
              <img
                src="/screenshots/screenshot-annotation.svg"
                alt="DocumentIQ annotation interface — draw bounding boxes on a PDF to teach the AI which regions of a document map to which extraction fields"
                width="1200"
                height="750"
                loading="lazy"
                decoding="async"
                className="w-full h-auto block"
              />
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium mb-4">
                <PenTool size={14} />
                Teach the AI
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-4">
                Annotate once,<br />extract accurately forever
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Draw a box on a representative PDF for each field — single value or
                multi-row table. The annotations become few-shot examples that
                dramatically improve accuracy across every similar document from
                that vendor, carrier, or format thereafter.
              </p>
              <ul className="space-y-3">
                {[
                  'Auto-detect raw text from PDF regions using PyMuPDF',
                  'Single-value boxes for headers; multi-row boxes for line-item tables',
                  'Annotations become ground-truth examples in the LLM prompt',
                  'Toggle annotations per extraction run for A/B testing',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-gray-600">
                    <Check size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Chat Highlight ─────────────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1 bg-white rounded-2xl border border-gray-200 p-6">
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-blue-600">U</span>
                  </div>
                  <div className="bg-gray-50 rounded-lg px-4 py-2.5 text-sm text-gray-700">
                    What's the average total across all invoices from Q1?
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles size={14} className="text-green-600" />
                  </div>
                  <div className="bg-gray-50 rounded-lg px-4 py-2.5 text-sm text-gray-700">
                    Based on the extracted data from 47 invoices in Q1, the average total is <strong>$3,842.50</strong>.
                    The highest was $12,400 from <span className="text-blue-600 underline">vendor_invoice_031.pdf</span> and
                    the lowest was $280 from <span className="text-blue-600 underline">misc_receipt_012.pdf</span>.
                  </div>
                </div>
                <div className="flex gap-2 ml-10">
                  <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-600 text-xs">Documents</span>
                  <span className="px-2 py-0.5 rounded bg-green-50 text-green-600 text-xs">Extracted Data</span>
                  <span className="px-2 py-0.5 rounded bg-purple-50 text-purple-600 text-xs">Both</span>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-medium mb-4">
                <MessageSquare size={14} />
                Project Chat
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-4">
                Ask questions across<br />all your documents
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Three context modes let you query raw documents, extracted data tables,
                or both. The AI cites specific documents, renders markdown tables,
                and you can export entire conversations as PDF.
              </p>
              <ul className="space-y-3">
                {[
                  'RAG-powered with vector search across document chunks',
                  'SQL generation for analytical queries over extracted data',
                  'Streaming responses with markdown rendering',
                  'Export conversations as PDF, Markdown, or JSON',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-gray-600">
                    <Check size={16} className="text-purple-600 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Enterprise Section ─────────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium mb-4">
            <Shield size={14} />
            Enterprise Ready
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-4">
            Built for teams and organizations
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-12">
            Multi-tenant architecture with role-based access, custom billing,
            and white-label branding. Deploy on Azure or on-premise.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: 'RBAC & Multi-Org', desc: 'Owner, admin, member, viewer, and billing roles per organization' },
              { icon: Zap, title: 'White-Label', desc: 'Custom logo, brand colors, and company name for each organization' },
              { icon: ScrollText, title: 'Custom Invoicing', desc: 'Enterprise billing with offline invoicing, no credit card required' },
              { icon: Cpu, title: 'Fine-Tuned Models', desc: 'Bring your own fine-tuned models via LiteLLM for domain-specific accuracy' },
            ].map((item) => (
              <div key={item.title} className="p-6 rounded-xl border border-gray-200 text-left">
                <item.icon size={20} className="text-blue-600 mb-3" />
                <h4 className="font-semibold text-gray-900 text-sm mb-1">{item.title}</h4>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Case Studies Teaser ──────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium mb-4">
              <TrendingUp size={14} />
              Real Results
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              Trusted across industries
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              See how teams extract structured data from thousands of documents at scale.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {caseStudies.slice(0, 2).map((study) => (
              <Link
                key={study.slug}
                to={`/case-studies/${study.slug}`}
                className="group flex flex-col rounded-2xl border border-gray-200 bg-white hover:border-blue-200 hover:shadow-lg hover:shadow-blue-50 transition-all duration-300 p-6 sm:p-8"
              >
                <span className="inline-flex self-start px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-3">
                  {study.industry}
                </span>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors leading-snug">
                  {study.headline}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1">
                  {study.summary}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{study.keyMetric.value}</div>
                    <div className="text-xs text-gray-500">{study.keyMetric.label}</div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 group-hover:gap-2 transition-all">
                    Read the full story
                    <ChevronRight size={16} />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              View all case studies
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Testimonials / Social Proof ─────────────────────── */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              What teams are saying
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                quote: 'We went from manually reviewing 1,200 contracts over three months to having every escalation clause extracted and queryable in under 48 hours. The sell-side recovery alone paid for the platform 10x over.',
                role: 'VP of Procurement',
                company: 'Global Mobility & Professional Services',
              },
              {
                quote: 'Our AP team was drowning in invoices from 200+ vendors in completely different formats. DocumentIQ handles all of them with a single field configuration. We eliminated manual data entry entirely.',
                role: 'Procurement Director',
                company: 'Manufacturing',
              },
              {
                quote: 'The chat feature changed how we work with extracted data. Instead of building reports, dispatchers just ask "which shipments to Dallas are overweight?" and get an instant answer with cited documents.',
                role: 'Operations Manager',
                company: 'Freight & Logistics',
              },
            ].map((t, i) => (
              <div key={i} className="flex flex-col p-6 sm:p-8 rounded-2xl border border-gray-200 bg-gray-50">
                <p className="text-sm text-gray-700 leading-relaxed flex-1 italic">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <p className="text-sm font-semibold text-gray-900">{t.role}</p>
                  <p className="text-xs text-gray-500">{t.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Enterprise Capabilities (fake-door validation) ─────── */}
      <EnterpriseCapabilities />

      {/* ── Pricing ────────────────────────────────────────────── */}
      <section id="pricing" className="py-20 sm:py-28 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              Simple, transparent pricing
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Start free, upgrade as you grow. No hidden fees.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-6 max-w-7xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`relative flex flex-col rounded-2xl border p-6 bg-white ${
                  plan.highlighted
                    ? 'border-blue-600 shadow-xl shadow-blue-100 ring-1 ring-blue-600'
                    : 'border-gray-200'
                }`}
              >
                {plan.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-medium">
                    {plan.badge}
                  </span>
                )}

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {plan.description}
                  </p>
                </div>

                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span className="text-gray-500 text-sm">{plan.period}</span>
                  )}
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-2.5 text-sm text-gray-600"
                    >
                      <Check
                        size={16}
                        className="text-blue-600 mt-0.5 flex-shrink-0"
                      />
                      {feature}
                    </li>
                  ))}
                </ul>

                {plan.ctaLink.startsWith('mailto') ? (
                  <a
                    href={plan.ctaLink}
                    className={`inline-flex items-center justify-center px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      plan.highlighted
                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {plan.cta}
                  </a>
                ) : (
                  <Link
                    to={plan.ctaLink}
                    className={`inline-flex items-center justify-center px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      plan.highlighted
                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {plan.cta}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────── */}
      <section id="faq" className="py-20 sm:py-28 bg-gray-50 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              Frequently asked questions
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Everything you need to know about DocumentIQ.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'Do I have to define every field from scratch?',
                a: 'No. DocumentIQ ships with 10 pre-built field templates for common document types — commercial invoices, purchase orders, contracts, price escalation clauses, bills of lading, packing declarations, bills of materials, certificates of analysis, FNOL insurance claims, and KYC documents. Apply one with a click and your project is ready to extract immediately. You can also save your own field configurations as custom templates for your organization.',
              },
              {
                q: 'How is DocumentIQ different from traditional OCR?',
                a: 'Traditional OCR converts images to text but doesn\'t understand what the text means. DocumentIQ sends entire documents to LLMs that understand context, tables, and relationships — extracting specific fields like invoice totals, contract clauses, or line items. It handles varied layouts, scanned documents, and complex tables without custom templates.',
              },
              {
                q: 'What file formats are supported?',
                a: 'DocumentIQ supports PDF, DOC, and DOCX files. PDFs are sent directly to the LLM (including scanned/image PDFs when using models with vision capabilities). Word documents are parsed and text-extracted automatically.',
              },
              {
                q: 'Which AI models can I use?',
                a: 'DocumentIQ supports multiple LLM providers through LiteLLM, including OpenAI (GPT-5, GPT-4o), Anthropic (Claude), and custom fine-tuned models. Your admin assigns which models are available on your billing plan, each with independent credit rates.',
              },
              {
                q: 'What are annotations and how do they improve accuracy?',
                a: 'Annotations let you draw bounding boxes on a PDF to show the AI where a value appears. You provide both the raw text (what\'s in the document) and the correct extraction (what the AI should return). These become few-shot examples that dramatically improve accuracy — like teaching a new employee where to look.',
              },
              {
                q: 'Can DocumentIQ extract line items and tables?',
                a: 'Yes. Multi-row extraction automatically detects repeating rows like invoice line items, BOM entries, or contract clauses. There are no row limits — it scales to hundreds of items per document.',
              },
              {
                q: 'How does the chat assistant work?',
                a: 'Each project has an AI chat with three context modes: Documents (searches raw text via RAG), Extracted Data (queries structured results), or Both. The assistant cites specific documents, renders markdown, and you can export conversations as PDF.',
              },
              {
                q: 'Is DocumentIQ suitable for enterprise use?',
                a: 'Yes. It offers multi-tenant architecture with RBAC, multi-org support, white-label branding, custom offline invoicing, fine-tuned model support, and is deployed on Azure with enterprise-grade security.',
              },
              {
                q: 'How much does it cost?',
                a: 'Billing is per page extracted. Free ($0 — 50 pages lifetime, no card required), Starter ($49/month — 500 pages, all standard LLMs, chat, annotations), Business ($399/month — 5,000 pages, premium LLMs, white-label), and Enterprise (custom — unlimited pages, fine-tuned models, SSO). Premium models like GPT-4o consume more credits per page than standard ones.',
              },
            ].map((faq, i) => (
              <details
                key={i}
                className="group rounded-xl border border-gray-200 bg-white"
              >
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors list-none">
                  {faq.q}
                  <ChevronRight size={16} className="text-gray-400 group-open:rotate-90 transition-transform flex-shrink-0 ml-4" />
                </summary>
                <div className="px-6 pb-4 text-sm text-gray-600 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ─────────────────────────────────────────── */}
      <section className="py-20 sm:py-24 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
            Ready to extract smarter?
          </h2>
          <p className="mt-4 text-lg text-blue-100">
            Join teams using DocumentIQ to turn unstructured documents into
            actionable, structured data — in minutes, not hours.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-blue-600 font-medium hover:bg-blue-50 transition-colors shadow-md text-sm sm:text-base"
            >
              Start for Free
              <ArrowRight size={18} />
            </Link>
            <a
              href="mailto:askus@algoscale.com"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-blue-300 text-white font-medium hover:bg-blue-500/20 transition-colors text-sm sm:text-base"
            >
              Talk to Sales
            </a>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <MarketingFooter />
    </div>
  )
}
